import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, Search, Loader2, 
  Crown, Calendar, Shield, MoreVertical, Edit2 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSubscriptions() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(null);
  const [grantData, setGrantData] = useState({ user_email: '', tier_id: '', duration_days: 30 });
  const [overrideData, setOverrideData] = useState({ tier_id: '', status: 'active' });
  const [saving, setSaving] = useState(false);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 20 });
      if (statusFilter) params.append('status', statusFilter);
      
      const res = await fetch(`${API}/api/admin/subscriptions?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API}/api/admin/tiers`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Failed to load tiers:', error);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const handleGrant = async () => {
    if (!grantData.user_email || !grantData.tier_id) {
      toast.error('Email and tier are required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('user_email', grantData.user_email);
      formData.append('tier_id', grantData.tier_id);
      formData.append('duration_days', grantData.duration_days);

      const res = await fetch(`${API}/api/admin/subscriptions/grant`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to grant');
      }

      toast.success('Subscription granted successfully');
      setShowGrantModal(false);
      setGrantData({ user_email: '', tier_id: '', duration_days: 30 });
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOverride = async () => {
    if (!overrideData.tier_id) {
      toast.error('Please select a tier');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('tier_id', overrideData.tier_id);
      formData.append('status', overrideData.status);

      const res = await fetch(`${API}/api/admin/subscriptions/${showOverrideModal}/override`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to override');
      }

      toast.success('Subscription overridden successfully');
      setShowOverrideModal(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      trialing: 'bg-blue-100 text-blue-700',
      past_due: 'bg-yellow-100 text-yellow-700',
      canceled: 'bg-red-100 text-red-700',
      incomplete: 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Button
            data-testid="admin-subs-back"
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold text-slate-900">User Subscriptions</h1>
            </div>
            <Button
              data-testid="grant-subscription-button"
              onClick={() => setShowGrantModal(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <Crown className="w-4 h-4 mr-2" />
              Grant Subscription
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="past_due">Past Due</option>
            <option value="canceled">Canceled</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No subscriptions found</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Tier</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Period End</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Cases Used</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {subscriptions.map((sub) => (
                  <tr key={sub.subscription_id} data-testid={`sub-row-${sub.subscription_id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-slate-900">{sub.user_name || 'Unknown'}</div>
                        <div className="text-sm text-slate-500">{sub.user_email || sub.user_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-500" />
                        <span className="font-medium capitalize">{sub.tier_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(sub.status)}`}>
                        {sub.status}
                      </span>
                      {sub.admin_granted && (
                        <span className="ml-2 text-xs text-slate-400">(Granted)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatDate(sub.current_period_end)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {sub.case_studies_used_this_month || 0}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        data-testid={`override-sub-${sub.subscription_id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowOverrideModal(sub.subscription_id);
                          setOverrideData({ tier_id: sub.tier_id, status: sub.status });
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center px-6 py-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Grant Modal */}
        {showGrantModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Grant Subscription</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">User Email *</label>
                  <Input
                    data-testid="grant-email-input"
                    type="email"
                    value={grantData.user_email}
                    onChange={(e) => setGrantData({ ...grantData, user_email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tier *</label>
                  <select
                    data-testid="grant-tier-select"
                    value={grantData.tier_id}
                    onChange={(e) => setGrantData({ ...grantData, tier_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="">Select Tier</option>
                    {tiers.map((tier) => (
                      <option key={tier.tier_id} value={tier.tier_id}>
                        {tier.display_name} (${tier.price_monthly}/mo)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Duration (days)</label>
                  <Input
                    data-testid="grant-duration-input"
                    type="number"
                    value={grantData.duration_days}
                    onChange={(e) => setGrantData({ ...grantData, duration_days: parseInt(e.target.value) || 30 })}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowGrantModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={handleGrant} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Grant Access'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Override Modal */}
        {showOverrideModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Override Subscription</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tier</label>
                  <select
                    data-testid="override-tier-select"
                    value={overrideData.tier_id}
                    onChange={(e) => setOverrideData({ ...overrideData, tier_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="">Select Tier</option>
                    {tiers.map((tier) => (
                      <option key={tier.tier_id} value={tier.tier_id}>
                        {tier.display_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select
                    data-testid="override-status-select"
                    value={overrideData.status}
                    onChange={(e) => setOverrideData({ ...overrideData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="active">Active</option>
                    <option value="canceled">Canceled</option>
                    <option value="past_due">Past Due</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowOverrideModal(null)}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={handleOverride} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Override'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
